import { useState } from 'react'

import { FeedbackFlowScreens } from '~/components/feedback/FeedbackFlowComponents'
import { FeedbackStorage } from '~/components/feedback/feedbackUtils'
import QueryLoader from '~/components/QueryLoader'
import { GameVariant, Platform, useGamePlayCountQuery, useGetUserQuery } from '~/graphql/generated/schema'
import { useEffectOnce } from '~/hooks/useEffectOnce'
import { useLaunchDarklyFlags } from '~/hooks/useFeatureFlags'
import logger from '~/utils/logger'

/**
 * Requirements:
 * When and where should this flow show up?
 * Segment: All Users who have completed at least 15 games (on flagship or L2)
 * Frequency: 1x only
 * After finishing a workout and the user lands on the today screen
 * After finishing a free play game and landing on the games screen
 *
 * ++ Place this feature behind a feature flag so that we could turn it off in the event that we need to
 */
export const FeedbackFlow = () => {
  const [showFeedback, setShowFeedback] = useState<boolean>(false)
  const { refetch } = useGamePlayCountQuery({ skip: true })
  const { ldFlags } = useLaunchDarklyFlags()
  const { data: userData, loading: userLoading, error: userError } = useGetUserQuery()

  const onCloseFeedback = () => {
    FeedbackStorage.setFeedbackShow() // feedbackShown=true will be stored
    setShowFeedback(false)
  }

  useEffectOnce(() => {
    async function processFeedback() {
      try {
        const feedback = FeedbackStorage.getFeedback()

        // If feedback has already been shown,then return
        if (feedback.feedbackShown) {
          return
        }
        // Return if the user has not completed a workout or a single gameplay
        if (!(feedback.workoutComplete || feedback.singleGameplayComplete)) {
          return
        }

        // if the user_review flag is not enabled, then return
        if (!ldFlags?.user_review) {
          return
        }

        const res = await refetch({
          filter: {
            platforms: [Platform.Web],
            variants: [GameVariant.Regular],
            partOfWorkout: false,
          },
        })
        const gamePlayCount = res.data?.me?.gamePlayCount ?? 0

        // if the user has completed X games, then show the feedback
        if (gamePlayCount >= 15) {
          setShowFeedback(true)
        }
      } catch (error) {
        logger.error('FeedbackFlow:processFeedback', error)
      }
    }
    processFeedback()
  })

  if (!showFeedback) return null
  return (
    <QueryLoader loading={userLoading} error={userError} data={userData}>
      <FeedbackFlowScreens onClose={onCloseFeedback} isFlagshipUser={!userData?.me?.originL2} />
    </QueryLoader>
  )
}
